/**
 * Plain words for pack codes the server sends: batch reasons (packable-batches), refusals of
 * plan / start / approve (422 / 409 errorCode + errors{reasons|problems|available_kg}) and run
 * states. Kept here — labels.ts is shared.
 */
import { humanize } from '../../labels'
import { readableApprovalProblem } from './labelForm'

const REASONS: Record<string, string> = {
  NOT_A_BATCH: 'It is not a machine batch.',
  ORDINARY_STREAM: 'It is ordinary produce, not our brand stream.',
  NOT_SINGLE_FARMER: 'It mixes more than one farmer.',
  NOT_COMMITTED: 'Its machine run has not been closed yet.',
  ON_HOLD: 'It is on hold.',
  NO_RUN: 'It did not come from a machine run.',
  UPSTREAM_NOT_BALANCED: 'Its machine run has not balanced yet.',
  NO_QC_SAMPLE: 'No sealed quality sample was taken from it.',
  NO_KILOS_LEFT: 'No kilos are left — other pack runs have taken it all.',
  LAB_FAIL: 'It failed a lab test.',
}

/** One batch reason ("ON_HOLD:LAB_FAIL", "STATUS_CONSUMED") as a sentence. */
export function batchReasonText(code: string): string {
  if (code.startsWith('ON_HOLD')) {
    const why = code.split(':')[1]
    return why ? `It is on hold (${humanize(why).toLowerCase()}).` : REASONS.ON_HOLD!
  }
  if (code.startsWith('STATUS_')) {
    const s = code.slice(7)
    const word: Record<string, string> = { CONSUMED: 'used up', VOID: 'void', DESTROYED: 'destroyed' }
    return `It is ${word[s] ?? humanize(s).toLowerCase()}.`
  }
  return REASONS[code] ?? `${humanize(code)}.`
}

const LAB: Record<string, string> = {
  PASS: 'Lab passed',
  FAIL: 'Lab failed',
  PENDING: 'Lab result pending',
  INCOMPLETE: 'Lab tests incomplete',
  NOT_ACCEPTED: 'Lab report not accepted',
}
export const labStatusText = (s: string | null | undefined) => (s ? (LAB[s] ?? humanize(s)) : 'Lab status unknown')

export interface PackRefusal {
  title: string
  lines: string[]
}

const CODES: Record<string, string> = {
  NO_QC_SAMPLE: 'This batch has no sealed quality sample, so it cannot be packed yet.',
  SKU_CROP_MISMATCH: 'The product is of another crop than the batch.',
  SKU_INCOMPLETE: 'The product has no shelf life set. Set it on the product first.',
  NOT_A_PACKING_HALL: 'The place picked is not a packing hall.',
  PREMISES_LICENCE_MISSING: 'The packing hall has no FSSAI licence in force on that day.',
  NO_APPROVED_LABEL: 'There is no approved label for this product at this hall. Approve one under Labels first.',
  RUN_ALREADY_STARTED: 'This pack run has already started — it is closed on the line, not cancelled.',
  LABEL_VERSION_MISMATCH: 'That label version is not the approved one for this product at this hall.',
  CLEANOUT_MISSING: 'The cleanout photo is missing.',
  ALLERGEN_CLEAN_MISSING: 'The last run on this line was another crop: do the full allergen clean and tick it.',
  LABEL_FROZEN: 'This label version is approved and can no longer be edited. Make a new version instead.',
  SKU_IN_USE: 'This product has pack lots and cannot be deleted.',
  GTIN_INVALID: 'The GTIN must have 8, 12, 13 or 14 digits with a valid check digit.',
  NOT_CLOSED: 'Carton labels can be printed once the run has been closed on the line.',
  WRONG_STATUS: 'It has changed meanwhile — reload the page.',
}

/**
 * A refused request as a title and plain lines. `code` is uc-core's errorCode, `errors` its
 * detail object, `message` its message (used for codes without a sentence of ours).
 */
export function packRefusal(code: string | undefined, message: string, errors: unknown): PackRefusal {
  const e = errors && typeof errors === 'object' && !Array.isArray(errors) ? (errors as Record<string, unknown>) : {}
  const list = (k: string) => (Array.isArray(e[k]) ? (e[k] as unknown[]).map(String) : [])
  switch (code) {
    case 'BATCH_NOT_PACKABLE':
      return { title: 'This batch cannot be packed', lines: list('reasons').map(batchReasonText) }
    case 'OVER_ALLOCATED': {
      const kg = typeof e.available_kg === 'string' ? e.available_kg : null
      return { title: kg ? `Only ${kg} kg of this batch is available` : 'Planned kilos must be above 0', lines: [] }
    }
    case 'SCALE_CHECK_FAILED':
      return { title: 'The scale check failed', lines: list('problems').map(scaleProblemText) }
    case 'LABEL_NOT_APPROVABLE':
      return { title: 'This label cannot be approved yet', lines: list('problems').map(readableApprovalProblem) }
    case 'LINE_BUSY':
      return { title: 'The pack line is busy', lines: [message] }
    default:
      return { title: (code && CODES[code]) || message, lines: [] }
  }
}

function scaleProblemText(p: string): string {
  if (/not a registered scale/.test(p)) return 'That equipment is not a registered scale or checkweigher.'
  if (/calibration is overdue/.test(p)) return 'Its calibration is overdue.'
  if (/stamp is overdue/.test(p)) return 'Its Legal Metrology stamp is overdue.'
  if (/scale not registered/.test(p)) return 'That scale is not registered or not in use.'
  const m = /reading ([\d.]+) g is more than ([\d.]+) g from ([\d.]+) g/.exec(p)
  if (m) return `The reading ${m[1]} g is more than ${m[2]} g away from the ${m[3]} g check weight.`
  return p
}

// ---- run states -------------------------------------------------------------

/** A pack run's state for people: OPEN splits into planned and started. */
export function runState(status: string, startedAt: string | null): { status: string; label: string } {
  if (status === 'OPEN') return startedAt ? { status: 'RUNNING', label: 'Started' } : { status: 'OPEN', label: 'Planned' }
  const words: Record<string, string> = {
    RUNNING: 'Packing',
    CLOSED: 'Closed',
    BALANCED: 'Balanced',
    EXCEPTION: 'Needs a decision',
    CANCELLED: 'Cancelled',
  }
  return { status, label: words[status] ?? humanize(status) }
}

export const RUN_STATUS_FILTER = [
  { value: 'OPEN', label: 'Planned or started' },
  { value: 'RUNNING', label: 'Packing' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'BALANCED', label: 'Balanced' },
  { value: 'EXCEPTION', label: 'Needs a decision' },
  { value: 'CANCELLED', label: 'Cancelled' },
] as const

const LOT_STATUS: Record<string, string> = {
  PACKING: 'Packing',
  CLOSED: 'Waiting for release',
  RELEASED: 'Released',
  WITHDRAWN: 'Withdrawn',
  DESTROYED: 'Destroyed',
}
export const packLotStatusText = (s: string) => LOT_STATUS[s] ?? humanize(s)

const LABEL_STATUS: Record<string, string> = { DRAFT: 'Draft', APPROVED: 'Approved', RETIRED: 'Retired' }
export const labelStatusText = (s: string) => LABEL_STATUS[s] ?? humanize(s)
